import { Form } from "antd";
import { useEffect, useRef, useState } from "react";
import { InternalFormInstance } from "rc-field-form/lib/interface";
import { HOOK_MARK } from "rc-field-form/lib/FieldContext";
import uniq from "lodash/uniq";

import { FormFieldMeta, UseFormFieldMeta } from "./types";

const useFormFieldMeta: UseFormFieldMeta = (nameMatcher) => {
  const form = Form.useFormInstance() as unknown as InternalFormInstance;
  const matcherRef = useRef(nameMatcher);

  const [meta, setMeta] = useState<FormFieldMeta>({
    name: [],
    touched: false,
    validating: false,
    errors: [],
    warnings: [],
    fieldErrors: [],
  });

  useEffect(() => {
    const { getInternalHooks } = form;
    const { registerField } = getInternalHooks(HOOK_MARK) || {};

    const privateMeta: FormFieldMeta = {
      name: [],
      touched: false,
      validating: false,
      errors: [],
      warnings: [],
      fieldErrors: form
        .getFieldsError()
        .filter(
          (error) => error.errors.length && matcherRef.current?.(error.name)
        ),
    };

    return registerField?.({
      props: {},
      onStoreChange(_store, namePathList, info) {
        if (!namePathList?.length) {
          return;
        }

        const namePathMatch = namePathList?.some((path) =>
          matcherRef.current?.(path)
        );

        if (!namePathMatch) {
          return;
        }

        const [namePath] = namePathList;

        switch (info.type) {
          case "setField": {
            if ("touched" in info.data) {
              privateMeta.touched = info.data.touched ?? false;
            }

            const newErrors = info.data.errors || [];
            const newWarnings = info.data.warnings || [];

            if ("errors" in info.data) {
              privateMeta.errors = uniq([...privateMeta.errors, ...newErrors]);
            }

            if ("warnings" in info.data) {
              privateMeta.warnings = uniq([
                ...privateMeta.warnings,
                ...newWarnings,
              ]);
            }

            privateMeta.fieldErrors = [
              ...privateMeta.fieldErrors,
              {
                name: namePath,
                errors: newErrors,
                warnings: newWarnings,
              },
            ];

            break;
          }

          case "validateFinish": {
            const fieldsError = form
              .getFieldsError()
              .filter(
                (error) =>
                  error.errors.length && matcherRef.current?.(error.name)
              );

            privateMeta.fieldErrors = [
              ...privateMeta.fieldErrors,
              ...fieldsError,
            ];
            privateMeta.errors = [
              ...privateMeta.errors,
              ...uniq(fieldsError.flatMap(({ errors }) => errors)),
            ];
            privateMeta.warnings = [
              ...privateMeta.warnings,
              ...uniq(fieldsError.flatMap(({ warnings }) => warnings)),
            ];

            break;
          }

          case "valueUpdate":
          case "reset":
            privateMeta.touched = false;
            privateMeta.errors = [];
            privateMeta.warnings = [];
            privateMeta.fieldErrors = [];

            break;

          default:
            return;
        }

        setMeta({ ...privateMeta });
      },
      getNamePath() {
        return privateMeta.name;
      },
      getMeta() {
        return privateMeta;
      },
      isFieldTouched(): boolean {
        return privateMeta.touched;
      },
      isFieldDirty(): boolean {
        return false;
      },
      isFieldValidating(): boolean {
        return false;
      },
      isListField(): boolean {
        return false;
      },
      isList(): boolean {
        return false;
      },
      isPreserve(): boolean {
        return false;
      },
      validateRules() {
        return Promise.resolve([]);
      },
      getErrors(): string[] {
        return privateMeta.errors;
      },
      getWarnings(): string[] {
        return privateMeta.warnings;
      },
    });
  }, [form]);

  useEffect(() => {
    matcherRef.current = nameMatcher;
  }, [nameMatcher]);

  return meta;
};

export default useFormFieldMeta;
